import React from "react";
import {
   Box,
   Button,
   Card,
   Flex,
   Form,
   H1,
   H2,
   Page,
   Spinner,
   Table,
   useI18nContext,
   P,
   Switch,
   FlexList,
   Required,
   Select,
} from "@procore/core-react";
import isBefore from "date-fns/isBefore";
import {
   ProjectDetailAlertTokens,
   scheduleAlertHourOptions,
   scheduleAlertMinuteOptions,
} from "../../communications/constants";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { Ban, Check } from "@procore/core-icons";
import { useGetPeopleAlertConfig } from "./queries";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useGetPeopleStream } from "@/react/components/common/queries/queries";
import {
   DynamicTokenEditor,
   type DynamicTokenEditorRef,
} from "../../communications/dynamic-tokens/dynamic-token-editor";
import type { FormikProps } from "formik";
import { useGetProjectAlertInfoQuery } from "../project/queries";
import {
   reconstructHtmlWithTokens,
   stripAndReturnHTMLTokens,
   validateAlertContent,
} from "../../communications/dynamic-tokens/token-utils";
import { AlertStore } from "@/stores/alert-store.core";
import { useToastAlertContext } from "@procore/toast-alert";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";

export type SendAssignmentAlertsTearsheetContentProps = {
   projectId: string;
   assignments: Array<{
      id: string;
      recipientIds: string[];
   }>;
   alertType: AlertContext;
   onClose: () => void;
};

type Recipient = {
   id: string;
   label: string;
   first_name: string;
   last_name: string;
   can_recieve_email: boolean;
   can_recieve_sms: boolean;
   can_recieve_mobile: boolean;
};

type AssignmentAlertFormValues = {
   additional_recipients: Recipient[];
   is_draft: boolean;
   scheduled_day: Date;
   hour: {
      id: number;
      value: number;
   };
   minute: {
      id: number;
      value: number;
   };
   day_night: {
      id: string;
      value: string;
   };
};

const RecipientsTable = (props: {
   recipients: Recipient[];
   loading: boolean;
   handleRemove: (id: string) => void;
}) => {
   const { recipients, loading, handleRemove } = props;
   const I18n = useI18nContext();
   return (
      <Spinner loading={loading}>
         <Table.Container>
            <Table>
               <Table.Header>
                  <Table.HeaderRow>
                     <Table.HeaderCell style={{ width: "40%" }}>
                        {I18n.t("views.company.workforce_planning.time_off.name")}
                     </Table.HeaderCell>
                     <Table.HeaderCell style={{ border: "none" }}>
                        {I18n.t("views.company.workforce_planning.people.email")}
                     </Table.HeaderCell>
                     <Table.HeaderCell style={{ border: "none" }}>
                        {I18n.t("views.company.workforce_planning.communications.sms")}
                     </Table.HeaderCell>
                     <Table.HeaderCell style={{ border: "none" }}>
                        {I18n.t("views.company.workforce_planning.communications.mobile")}
                     </Table.HeaderCell>
                     {/* The blank column cell below will be for a "Remove" button in the table body */}
                     <Table.HeaderCell style={{ border: "none" }} />
                  </Table.HeaderRow>
               </Table.Header>
               <Table.Body>
                  {recipients.map((recipient: Recipient) => (
                     <Table.BodyRow key={`recipient-${recipient.id}`}>
                        <Table.BodyCell style={{ width: "50%" }}>
                           <Table.TextCell>
                              <P>{recipient.label}</P>
                           </Table.TextCell>
                        </Table.BodyCell>
                        <Table.BodyCell>
                           <Flex justifyContent="center">
                              <Table.IconCell>
                                 {recipient.can_recieve_email ? <Check /> : <Ban />}
                              </Table.IconCell>
                           </Flex>
                        </Table.BodyCell>
                        <Table.BodyCell>
                           <Flex justifyContent="center">
                              <Table.IconCell>
                                 {recipient.can_recieve_sms ? <Check /> : <Ban />}
                              </Table.IconCell>
                           </Flex>
                        </Table.BodyCell>
                        <Table.BodyCell>
                           <Flex justifyContent="center">
                              <Table.IconCell>
                                 {recipient.can_recieve_mobile ? <Check /> : <Ban />}
                              </Table.IconCell>
                           </Flex>
                        </Table.BodyCell>
                        <Table.BodyCell style={{ width: "30%" }}>
                           <Flex justifyContent="center">
                              <Button
                                 variant="secondary"
                                 onClick={() => handleRemove(recipient.id)}
                                 aria-label={`${I18n.t(
                                    "views.company.workforce_planning.remove",
                                 )} ${recipient.label}`}
                              >
                                 {I18n.t("views.company.workforce_planning.remove")}
                              </Button>
                           </Flex>
                        </Table.BodyCell>
                     </Table.BodyRow>
                  ))}
               </Table.Body>
            </Table>
         </Table.Container>
      </Spinner>
   );
};

type GetBatchAlertsPayloadParams = {
   values: AssignmentAlertFormValues;
   alertContent: { subject: string; content: string };
   projectId: string;
   assignments: Array<{ id: string; recipientIds: string[] }>;
   allRecipients: Recipient[];
   removedRecipientIds: Set<string>;
   shouldScheduleDelivery: boolean;
};

function getBatchAlertsPayload(params: GetBatchAlertsPayloadParams) {
   const {
      values,
      alertContent,
      projectId,
      assignments,
      allRecipients,
      removedRecipientIds,
      shouldScheduleDelivery,
   } = params;
   const { content: apiReadySubject, dynamicTokens: subjectTokens } = stripAndReturnHTMLTokens(
      alertContent.subject,
      projectId,
   );
   const { content: apiReadyContent, dynamicTokens: contentTokens } = stripAndReturnHTMLTokens(
      alertContent.content,
      projectId,
   );
   const dynamicTokens = subjectTokens.concat(contentTokens);

   const allAssignedResources = assignments.flatMap((assignment) => assignment.recipientIds);
   const allAdditionalRecipientIds = allRecipients
      .filter((recipient: Recipient) => !allAssignedResources.includes(recipient.id))
      .filter((recipient: Recipient) => !removedRecipientIds.has(recipient.id)) // Exclude removed additionals
      .map((recipient: Recipient) => recipient.id);

   const payload: any = {
      project_id: projectId,
      context: shouldScheduleDelivery ? AlertContext.SCHEDULED : AlertContext.OPEN,
      template: {
         content: apiReadyContent,
         subject: apiReadySubject,
         include_signature: false,
         dynamic_tokens: dynamicTokens,
      },
      assignments: assignments.map((assignment) => ({
         id: assignment.id,
         recipient_ids: [
            // Filter out removed recipients from original list
            ...assignment.recipientIds.filter((id) => !removedRecipientIds.has(id)),
            // Add additional recipients (already filtered above)
            ...allAdditionalRecipientIds,
         ],
      })),
   };

   if (shouldScheduleDelivery) {
      payload.context = AlertContext.SCHEDULED;
      payload.scheduled_day = getDetachedDay(values.scheduled_day);
      payload.scheduled_time =
         values.day_night.value === "pm"
            ? values.hour.value + values.minute.value + 12
            : values.hour.value + values.minute.value;
   }

   return payload;
}

export const SendAssignmentAlertsTearsheetContent = (
   props: SendAssignmentAlertsTearsheetContentProps,
) => {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const { projectId, assignments, alertType, onClose } = props;
   const [shouldScheduleDelivery, setShouldScheduleDelivery] = React.useState<boolean>(false);
   const [allRecipients, setAllRecipients] = React.useState<Recipient[]>([]);
   const [initialAlertContent, setInitialAlertContent] = React.useState({
      subject: "",
      content: "",
   });
   const [alertContent, setAlertContent] = React.useState({ subject: "", content: "" });
   const [removedRecipientIds, setRemovedRecipientIds] = React.useState<Set<string>>(new Set());
   const [errorFields, setErrorFields] = React.useState<string[]>([]);
   const editorRef = React.useRef<DynamicTokenEditorRef>(null);
   const { data: initialRecipients, isLoading: initialRecipientsLoading } = useGetPeopleAlertConfig(
      assignments.flatMap((assignment) => assignment.recipientIds),
   );
   const { data: peopleOptions, isLoading: peopleOptionsLoading } = useGetPeopleStream(
      groupId != "my-groups" ? groupId : undefined,
   );
   const { data: projectAlertInfo } = useGetProjectAlertInfoQuery(projectId, alertType);
   const { showToast } = useToastAlertContext();

   React.useEffect(() => {
      if (initialRecipients && initialRecipients.data) {
         setAllRecipients(
            initialRecipients.data.map((recipient: Recipient) => ({
               ...recipient,
               label: `${recipient.first_name} ${recipient.last_name}`,
            })) ?? [],
         );
      }
   }, [initialRecipients]);

   React.useEffect(() => {
      if (projectAlertInfo && projectAlertInfo.data) {
         if (projectAlertInfo.data.template) {
            const initialSubject = reconstructHtmlWithTokens(
               projectAlertInfo.data.template.dynamic_tokens,
               projectAlertInfo.data.template.subject ?? "",
            );
            const initialContent = reconstructHtmlWithTokens(
               projectAlertInfo.data.template.dynamic_tokens,
               projectAlertInfo.data.template.content ?? "",
            );
            setInitialAlertContent({
               subject: initialSubject,
               content: initialContent,
            });
            setAlertContent({
               subject: initialSubject,
               content: initialContent,
            });
         }
      }
   }, [projectAlertInfo]);

   async function handleSubmit(values: AssignmentAlertFormValues) {
      const newErrorFields = validateAlertContent(alertContent);
      if (newErrorFields.length > 0) {
         setErrorFields(newErrorFields);
         return;
      }

      try {
         const payload = getBatchAlertsPayload({
            values,
            alertContent,
            projectId,
            assignments,
            allRecipients,
            removedRecipientIds,
            shouldScheduleDelivery,
         });
         await AlertStore.batchSendAssignmentAlerts(payload).payload;
         showToast.success(
            I18n.t("views.company.workforce_planning.communications.alert_toasts.sent_success"),
         );
         onClose();
      } catch (error) {
         reportError(
            error as NotifiableError,
            "send-assignment-alerts-tearsheet-content__handle-submit",
         );
         showToast.error(
            I18n.t("views.company.workforce_planning.communications.alert_toasts.failure"),
         );
      }
   }

   async function handleSaveDraft(values: AssignmentAlertFormValues) {
      const newErrorFields = validateAlertContent(alertContent);
      if (newErrorFields.length > 0) {
         setErrorFields(newErrorFields);
         return;
      }

      try {
         const payload = getBatchAlertsPayload({
            values,
            alertContent,
            projectId,
            assignments,
            allRecipients,
            removedRecipientIds,
            shouldScheduleDelivery: false,
         });
         payload.context = AlertContext.DRAFTS;
         await AlertStore.batchSendAssignmentAlerts(payload).payload;
         showToast.success(
            I18n.t("views.company.workforce_planning.communications.alert_toasts.draft_success"),
         );
         onClose();
      } catch (error) {
         reportError(
            error as NotifiableError,
            "send-assignment-alerts-tearsheet-content__handle-save-draft",
         );
         showToast.error(
            I18n.t("views.company.workforce_planning.communications.alert_toasts.draft_failure"),
         );
      }
   }

   const handleRemoveRecipient = (id: string) => {
      setRemovedRecipientIds((prev) => new Set([...prev, id]));
      setAllRecipients((prev) => prev.filter((r) => r.id !== id));
   };

   return (
      <Page>
         <Page.Main
            style={{
               width: "952px",
               display: "flex",
               flexDirection: "column",
            }}
         >
            <Page.Header>
               <Page.Title>
                  <H1>
                     {I18n.t("views.company.workforce_planning.communications.assignment_alert")}
                  </H1>
               </Page.Title>
            </Page.Header>
            <Form
               view="create"
               onSubmit={handleSubmit}
               initialValues={{
                  additional_recipients: [],
                  is_draft: false,
                  scheduled_day: new Date(),
                  hour: {
                     id: 12,
                     value: 12,
                  },
                  minute: {
                     id: 0,
                     value: 0,
                  },
                  day_night: {
                     id: "am",
                     value: "am",
                  },
               }}
            >
               {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
               {({ isSubmitting, submitForm, values }: FormikProps<any>) => (
                  <>
                     <Page.Body style={{ width: "100%", marginBottom: "auto" }}>
                        <Card>
                           <Box padding="md" style={{ paddingBottom: "8px" }}>
                              <H2>
                                 {I18n.t(
                                    "views.company.workforce_planning.communications.recipients",
                                 )}
                              </H2>
                              <Form.Form>
                                 <Form.Row>
                                    <Select
                                       placeholder={I18n.t(
                                          "views.company.workforce_planning.communications.select_person_to_add",
                                       )}
                                       loading={peopleOptionsLoading}
                                       onSelect={(value: any) => {
                                          const selectedId = peopleOptions?.find(
                                             (o) => o.id === value.item,
                                          );
                                          if (selectedId) {
                                             setAllRecipients((prev) => [...prev, selectedId]);

                                             setRemovedRecipientIds((prev) => {
                                                const next = new Set(prev);
                                                next.delete(selectedId.id);
                                                return next;
                                             });
                                          }
                                       }}
                                    >
                                       {peopleOptions?.map((option) => (
                                          <Select.Option
                                             key={option.id}
                                             value={option.id}
                                             selected={allRecipients.some(
                                                (r) => r.id === option.id,
                                             )}
                                          >
                                             {option.label}
                                          </Select.Option>
                                       ))}
                                    </Select>
                                 </Form.Row>
                                 <RecipientsTable
                                    recipients={allRecipients}
                                    loading={initialRecipientsLoading}
                                    handleRemove={handleRemoveRecipient}
                                 />
                                 <Flex gap="sm" paddingTop="md">
                                    <Switch
                                       aria-label={I18n.t(
                                          "views.company.workforce_planning.communications.schedule_delivery",
                                       )}
                                       checked={shouldScheduleDelivery}
                                       onChange={() =>
                                          setShouldScheduleDelivery(!shouldScheduleDelivery)
                                       }
                                    />
                                    <P>
                                       {I18n.t(
                                          "views.company.workforce_planning.communications.schedule_delivery",
                                       )}
                                    </P>
                                 </Flex>
                                 {shouldScheduleDelivery && (
                                    <Form.Row>
                                       <Form.DateSelect
                                          colStart={1}
                                          name="scheduled_day"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.date",
                                          )}
                                          disabledDate={(date, today) => isBefore(date, today)}
                                          required
                                       />
                                       <Form.Select
                                          colStart={4}
                                          colWidth={3}
                                          name="hour"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.hour",
                                          )}
                                          options={scheduleAlertHourOptions}
                                          onSearch={false}
                                          required
                                       />
                                       <Form.Select
                                          colStart={7}
                                          colWidth={3}
                                          name="minute"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.minute",
                                          )}
                                          options={scheduleAlertMinuteOptions}
                                          onSearch={false}
                                          required
                                       />
                                       <Form.RadioButtons
                                          className="border-box flex"
                                          colStart={10}
                                          colWidth={2}
                                          name="day_night"
                                          label={I18n.t(
                                             "views.company.workforce_planning.communications.day_night",
                                          )}
                                          tooltip={I18n.t(
                                             "views.company.workforce_planning.communications.day_night_tooltip",
                                          )}
                                          options={[
                                             {
                                                id: "am",
                                                label: "AM",
                                                value: "am",
                                             },
                                             {
                                                id: "pm",
                                                label: "PM",
                                                value: "pm",
                                             },
                                          ]}
                                       />
                                    </Form.Row>
                                 )}
                              </Form.Form>
                           </Box>
                        </Card>
                        <Box paddingTop="md">
                           <DynamicTokenEditor
                              ref={editorRef}
                              tokens={ProjectDetailAlertTokens}
                              I18n={I18n}
                              onContentChange={setAlertContent}
                              initialContent={initialAlertContent}
                              errorFields={errorFields}
                           />
                        </Box>
                     </Page.Body>
                     <Page.Footer>
                        <Box padding="md">
                           <Flex justifyContent="space-between" alignItems="center">
                              <Required showLabel />
                              <FlexList space="sm">
                                 <Button
                                    variant="tertiary"
                                    disabled={isSubmitting}
                                    onClick={onClose}
                                 >
                                    {I18n.t("views.company.workforce_planning.cancel")}
                                 </Button>
                                 <Button
                                    variant="secondary"
                                    onClick={() => handleSaveDraft(values)}
                                 >
                                    {I18n.t(
                                       "views.company.workforce_planning.communications.save_draft",
                                    )}
                                 </Button>
                                 <Button type="submit" disabled={isSubmitting} onClick={submitForm}>
                                    {shouldScheduleDelivery
                                       ? I18n.t(
                                            "views.company.workforce_planning.communications.schedule",
                                         )
                                       : I18n.t(
                                            "views.company.workforce_planning.communications.send",
                                         )}
                                 </Button>
                              </FlexList>
                           </Flex>
                        </Box>
                     </Page.Footer>
                  </>
               )}
            </Form>
         </Page.Main>
      </Page>
   );
};
